import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { of } from "rxjs";
import { catchError, concatMap, map, switchMap, withLatestFrom } from "rxjs/operators";
import { Store } from "@ngrx/store";
import {
    CatalogClient,
    CatalogProductClient,
    CatalogProductOperationResponse,
    CatalogProductType,
} from "src/api-service";
import { selectSelectedCompany } from "../../user/user.selectors";
import { CatalogActions } from "./catalog.actions";
import { normalizeError } from "../../app-store-error.types";
import { selectCatalogId } from "./catalog.selectors";

// **************************************************************************
// HELPER: Create error response for network errors
// **************************************************************************
function createNetworkErrorResponse(error: unknown): CatalogProductOperationResponse {
    const appError = normalizeError(error);
    return {
        latestOperation: {
            id: 0,
            fieldName: undefined,
            entityType: CatalogProductType.Group,
            wasSuccessful: false,
            errorMessage: appError.message,
            changedFields: [],
            warnings: [],
        },
        products: undefined,
        validationErrors: [],
    };
}

@Injectable()
export class CatalogEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly catalogClient = inject(CatalogClient);
    private readonly catalogProductClient = inject(CatalogProductClient);

    // **************************************************************************
    // LOAD CATALOG (select and load full details)
    // If financingCompanyId is provided, uses pricing endpoint to recalculate
    // **************************************************************************
    loadCatalog$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CatalogActions.loadCatalog),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ catalogId, financingCompanyId }, selectedCompany]) => {
                const companyId = String(selectedCompany.id);
                // Choose endpoint based on whether financingCompanyId is provided
                const request$ = financingCompanyId? this.catalogClient.getWithPricing(companyId, catalogId, financingCompanyId) : this.catalogClient.get(companyId, catalogId);
                return request$.pipe(
                    map(response => CatalogActions.loadCatalogSuccess({
                        catalog: response,
                    })),
                    catchError(error =>
                        of(
                            CatalogActions.loadCatalogFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN.SUPER_USER.CATALOGS.TOAST.LOAD_ERROR",
                            }),
                        ),
                    ),
                );
            }),
        ),
    );

    // **************************************************************************
    // UPDATE CATALOG SETTINGS
    // **************************************************************************
    updateCatalog$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CatalogActions.updateCatalog),
            withLatestFrom(
                this.store.select(selectSelectedCompany),
                this.store.select(selectCatalogId),
            ),
            switchMap(([{ id, request }, selectedCompany, catalogId]) =>
                this.catalogClient.update(String(selectedCompany.id), id ?? catalogId, request).pipe(
                    map(catalog =>
                        CatalogActions.updateCatalogSuccess({
                            // Convert Catalog to CatalogResponse format
                            catalog: { ...catalog, currencyCode: catalog.currencyCode ?? "SEK" },
                            toastKey: "ADMIN.SUPER_USER.CATALOGS.TOAST.UPDATE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            CatalogActions.updateCatalogFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN.SUPER_USER.CATALOGS.TOAST.UPDATE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    // **************************************************************************
    // TOGGLE LOCK CUSTOMER PRICES
    // **************************************************************************
    toggleLockCustomerPrices$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CatalogActions.toggleLockCustomerPrices),
            withLatestFrom(
                this.store.select(selectSelectedCompany),
                this.store.select(selectCatalogId),
            ),
            switchMap(([{ lockCustomerPrices }, selectedCompany, catalogId]) =>
                this.catalogClient
                    .lockPrices(String(selectedCompany.id), catalogId, lockCustomerPrices)
                    .pipe(
                        map(catalog =>
                            CatalogActions.toggleLockCustomerPricesSuccess({
                                // Convert Catalog to CatalogResponse format
                                catalog: { ...catalog, currencyCode: catalog.currencyCode ?? "SEK" },
                                toastKey: lockCustomerPrices
                                    ? "ADMIN.SUPER_USER.CATALOGS.TOAST.PRICES_LOCKED"
                                    : "ADMIN.SUPER_USER.CATALOGS.TOAST.PRICES_UNLOCKED",
                            }),
                        ),
                        catchError(error =>
                            of(
                                CatalogActions.toggleLockCustomerPricesFailure({
                                    error: normalizeError(error),
                                    toastKey: "ADMIN.SUPER_USER.CATALOGS.TOAST.TOGGLE_LOCK_ERROR",
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    // **************************************************************************
    // TOGGLE INCLUDE CUSTOMER PRICE DECIMALS
    // **************************************************************************
    toggleIncludeCustomerPriceDecimals$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CatalogActions.toggleIncludeCustomerPriceDecimals),
            withLatestFrom(
                this.store.select(selectSelectedCompany),
                this.store.select(selectCatalogId),
            ),
            switchMap(([{ includeDecimals }, selectedCompany, catalogId]) =>
                this.catalogClient
                    .includeDecimals(String(selectedCompany.id), catalogId, includeDecimals)
                    .pipe(
                        map(catalog =>
                            CatalogActions.toggleIncludeCustomerPriceDecimalsSuccess({
                                catalog: { ...catalog, currencyCode: catalog.currencyCode ?? "SEK" },
                                toastKey: includeDecimals
                                    ? "ADMIN.SUPER_USER.CATALOGS.TOAST.DECIMALS_ENABLED"
                                    : "ADMIN.SUPER_USER.CATALOGS.TOAST.DECIMALS_DISABLED",
                            }),
                        ),
                        catchError(error =>
                            of(
                                CatalogActions.toggleIncludeCustomerPriceDecimalsFailure({
                                    error: normalizeError(error),
                                    toastKey: "ADMIN.SUPER_USER.CATALOGS.TOAST.TOGGLE_DECIMALS_ERROR",
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    // **************************************************************************
    // CREATE ENTITY (unified via CatalogProductClient)
    // **************************************************************************
    createEntity$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CatalogActions.createEntity),
            withLatestFrom(
                this.store.select(selectSelectedCompany),
                this.store.select(selectCatalogId),
            ),
            concatMap(([{ request }, selectedCompany, catalogId]) => {
                const companyId = String(selectedCompany.id);

                return this.catalogProductClient
                    .create(companyId, catalogId, request)
                    .pipe(
                        map(result => {
                            const toastKey = this.getCreateToastKey(request.entityType, true);
                            return result.latestOperation.wasSuccessful
                                ? CatalogActions.createEntitySuccess({ response: result, toastKey })
                                : CatalogActions.createEntityFailure({ response: result, toastKey: this.getCreateToastKey(request.entityType, false) });
                        }),
                        catchError(error =>
                            of(
                                CatalogActions.createEntityFailure({
                                    response: createNetworkErrorResponse(error),
                                    toastKey: this.getCreateToastKey(request.entityType, false),
                                }),
                            ),
                        ),
                    );
            }),
        ),
    );

    // **************************************************************************
    // UPDATE ENTITY FIELD (unified via CatalogProductClient)
    // **************************************************************************
    updateEntityField$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CatalogActions.updateEntityField),
            withLatestFrom(
                this.store.select(selectSelectedCompany),
                this.store.select(selectCatalogId),
            ),
            concatMap(([{ request }, selectedCompany, catalogId]) => {
                const companyId = String(selectedCompany.id);

                return this.catalogProductClient
                    .updateField(companyId, catalogId, request)
                    .pipe(
                        map(result =>
                            result.latestOperation.wasSuccessful
                                ? CatalogActions.updateEntityFieldSuccess({ response: result })
                                : CatalogActions.updateEntityFieldFailure({
                                      response: result,
                                      toastKey: "ADMIN.SUPER_USER.CATALOGS.TOAST.UPDATE_FIELD_ERROR",
                                  }),
                        ),
                        catchError(error =>
                            of(
                                CatalogActions.updateEntityFieldFailure({
                                    response: createNetworkErrorResponse(error),
                                    toastKey: "ADMIN.SUPER_USER.CATALOGS.TOAST.UPDATE_FIELD_ERROR",
                                }),
                            ),
                        ),
                    );
            }),
        ),
    );

    // **************************************************************************
    // DELETE ENTITY (unified via CatalogProductClient)
    // **************************************************************************
    deleteEntity$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CatalogActions.deleteEntity),
            withLatestFrom(
                this.store.select(selectSelectedCompany),
                this.store.select(selectCatalogId),
            ),
            concatMap(([{ request }, selectedCompany, catalogId]) => {
                const companyId = String(selectedCompany.id);

                return this.catalogProductClient
                    .delete(companyId, catalogId, request)
                    .pipe(
                        map(result => {
                            const toastKey = this.getDeleteToastKey(request.entityType, true);
                            return result.latestOperation.wasSuccessful
                                ? CatalogActions.deleteEntitySuccess({ response: result, toastKey })
                                : CatalogActions.deleteEntityFailure({ response: result, toastKey: this.getDeleteToastKey(request.entityType, false) });
                        }),
                        catchError(error =>
                            of(
                                CatalogActions.deleteEntityFailure({
                                    response: createNetworkErrorResponse(error),
                                    toastKey: this.getDeleteToastKey(request.entityType, false),
                                }),
                            ),
                        ),
                    );
            }),
        ),
    );

    // **************************************************************************
    // UPLOAD ENTITY IMAGE
    // **************************************************************************
    uploadEntityImage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CatalogActions.uploadEntityImage),
            withLatestFrom(
                this.store.select(selectSelectedCompany),
                this.store.select(selectCatalogId),
            ),
            concatMap(([{ entityType, entityId, file }, selectedCompany, catalogId]) => {
                const companyId = String(selectedCompany.id);

                return this.catalogProductClient
                    .uploadImage(companyId, catalogId, entityId, entityType, { data: file, fileName: file.name })
                    .pipe(
                        map(result =>
                            result.latestOperation.wasSuccessful
                                ? CatalogActions.uploadEntityImageSuccess({
                                      response: result,
                                      toastKey: "ADMIN.SUPER_USER.CATALOGS.TOAST.IMAGE_UPLOAD_SUCCESS",
                                  })
                                : CatalogActions.uploadEntityImageFailure({
                                      response: result,
                                      toastKey: "ADMIN.SUPER_USER.CATALOGS.TOAST.IMAGE_UPLOAD_ERROR",
                                  }),
                        ),
                        catchError(error =>
                            of(
                                CatalogActions.uploadEntityImageFailure({
                                    response: createNetworkErrorResponse(error),
                                    toastKey: "ADMIN.SUPER_USER.CATALOGS.TOAST.IMAGE_UPLOAD_ERROR",
                                }),
                            ),
                        ),
                    );
            }),
        ),
    );

    // **************************************************************************
    // DUPLICATE ENTITY
    // **************************************************************************
    duplicateEntity$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CatalogActions.duplicateEntity),
            withLatestFrom(
                this.store.select(selectSelectedCompany),
                this.store.select(selectCatalogId),
            ),
            concatMap(([{ request }, selectedCompany, catalogId]) => {
                const companyId = String(selectedCompany.id);

                return this.catalogProductClient
                    .duplicate(companyId, catalogId, request)
                    .pipe(
                        map(result => {
                            const toastKey = this.getDuplicateToastKey(request.entityType, true);
                            return result.latestOperation.wasSuccessful
                                ? CatalogActions.duplicateEntitySuccess({ response: result, toastKey })
                                : CatalogActions.duplicateEntityFailure({
                                      response: result,
                                      toastKey: this.getDuplicateToastKey(request.entityType, false),
                                  });
                        }),
                        catchError(error =>
                            of(
                                CatalogActions.duplicateEntityFailure({
                                    response: createNetworkErrorResponse(error),
                                    toastKey: this.getDuplicateToastKey(request.entityType, false),
                                }),
                            ),
                        ),
                    );
            }),
        ),
    );

    // **************************************************************************
    // PASTE ENTITY
    // **************************************************************************
    pasteEntity$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CatalogActions.pasteEntity),
            withLatestFrom(
                this.store.select(selectSelectedCompany),
                this.store.select(selectCatalogId),
            ),
            concatMap(([{ request }, selectedCompany, catalogId]) => {
                const companyId = String(selectedCompany.id);

                return this.catalogProductClient
                    .paste(companyId, catalogId, request)
                    .pipe(
                        map(result =>
                            result.latestOperation.wasSuccessful
                                ? CatalogActions.pasteEntitySuccess({
                                      response: result,
                                      toastKey: "ADMIN.SUPER_USER.CATALOGS.TOAST.PASTE_SUCCESS",
                                  })
                                : CatalogActions.pasteEntityFailure({
                                      response: result,
                                      toastKey: "ADMIN.SUPER_USER.CATALOGS.TOAST.PASTE_ERROR",
                                  }),
                        ),
                        catchError(error =>
                            of(
                                CatalogActions.pasteEntityFailure({
                                    response: createNetworkErrorResponse(error),
                                    toastKey: "ADMIN.SUPER_USER.CATALOGS.TOAST.PASTE_ERROR",
                                }),
                            ),
                        ),
                    );
            }),
        ),
    );

    // **************************************************************************
    // BULK PUBLISH/UNPUBLISH ALL PRODUCTS
    // **************************************************************************
    bulkPublishProducts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CatalogActions.bulkPublishProducts),
            withLatestFrom(
                this.store.select(selectSelectedCompany),
                this.store.select(selectCatalogId),
            ),
            switchMap(([{ publish }, selectedCompany, catalogId]) => {
                const companyId = String(selectedCompany.id);
                const toastKeySuccess = publish
                    ? "ADMIN.SUPER_USER.CATALOGS.TOAST.PUBLISH_ALL_SUCCESS"
                    : "ADMIN.SUPER_USER.CATALOGS.TOAST.UNPUBLISH_ALL_SUCCESS";
                const toastKeyError = publish
                    ? "ADMIN.SUPER_USER.CATALOGS.TOAST.PUBLISH_ALL_ERROR"
                    : "ADMIN.SUPER_USER.CATALOGS.TOAST.UNPUBLISH_ALL_ERROR";

                return this.catalogClient
                    .bulkPublish(companyId, catalogId, { publish })
                    .pipe(
                        map(result =>
                            CatalogActions.bulkPublishProductsSuccess({
                                response: result,
                                toastKey: toastKeySuccess,
                            }),
                        ),
                        catchError(error =>
                            of(
                                CatalogActions.bulkPublishProductsFailure({
                                    error: normalizeError(error),
                                    toastKey: toastKeyError,
                                }),
                            ),
                        ),
                    );
            }),
        ),
    );

    // **************************************************************************
    // HELPER METHODS
    // **************************************************************************
    private getCreateToastKey(entityType: CatalogProductType, success: boolean): string {
        const base = "ADMIN.SUPER_USER.CATALOGS.TOAST.CREATE_";
        const suffix = success ? "_SUCCESS" : "_ERROR";
        switch (entityType) {
            case CatalogProductType.Group:
                return base + "GROUP" + suffix;
            case CatalogProductType.HeadProduct:
                return base + "HEAD_PRODUCT" + suffix;
            case CatalogProductType.Product:
                return base + "PRODUCT" + suffix;
            case CatalogProductType.SubProduct:
                return base + "SUBPRODUCT" + suffix;
            default:
                return base + "ERROR";
        }
    }

    private getDeleteToastKey(entityType: CatalogProductType, success: boolean): string {
        const base = "ADMIN.SUPER_USER.CATALOGS.TOAST.DELETE_";
        const suffix = success ? "_SUCCESS" : "_ERROR";
        switch (entityType) {
            case CatalogProductType.Group:
                return base + "GROUP" + suffix;
            case CatalogProductType.HeadProduct:
                return base + "HEAD_PRODUCT" + suffix;
            case CatalogProductType.Product:
                return base + "PRODUCT" + suffix;
            case CatalogProductType.SubProduct:
                return base + "SUBPRODUCT" + suffix;
            default:
                return base + "ERROR";
        }
    }

    private getDuplicateToastKey(entityType: CatalogProductType, success: boolean): string {
        if (!success) {
            return "ADMIN.SUPER_USER.CATALOGS.TOAST.DUPLICATE_ERROR";
        }
        switch (entityType) {
            case CatalogProductType.Group:
                return "ADMIN.SUPER_USER.CATALOGS.TOAST.DUPLICATE_GROUP_SUCCESS";
            case CatalogProductType.HeadProduct:
                return "ADMIN.SUPER_USER.CATALOGS.TOAST.DUPLICATE_HEAD_PRODUCT_SUCCESS";
            case CatalogProductType.Product:
                return "ADMIN.SUPER_USER.CATALOGS.TOAST.DUPLICATE_PRODUCT_SUCCESS";
            case CatalogProductType.SubProduct:
                return "ADMIN.SUPER_USER.CATALOGS.TOAST.DUPLICATE_SUBPRODUCT_SUCCESS";
            default:
                return "ADMIN.SUPER_USER.CATALOGS.TOAST.DUPLICATE_ERROR";
        }
    }
}
